/**
 * Abilities (doc 07). The player is the god-view, and these four are the only
 * things they touch directly — everything else is a building decision.
 *
 * They cost STORED RESOURCES, which is the design: your economy is the mana
 * bar, so a volley is turret food you chose not to feed a turret. Pure
 * functions over a narrow host, like combat and economy (doc 12 §Code health);
 * `Sim.doCast` owns the cooldown, the price and the meta gate, and calls in.
 */

import abilitiesJson from '../content/abilities.json' with { type: 'json' };
import { TICK_RATE } from './const.ts';
import { structCentre } from './economy.ts';
import type { Rng } from './rng.ts';
import { SPECIES } from './species.ts';
import type { SimGrid } from './grid.ts';
import type { AbilityId, Enemy, SimEvent, SimState, Structure } from './types.ts';

/** What an ability needs from the sim — nothing more. */
export interface AbilityHost {
  state: SimState;
  grid: SimGrid;
  rng: Rng;
  emit(ev: SimEvent): void;
  byId(id: number): Structure | undefined;
  /** Applies damage and returns what actually landed (doc 17 §7 numbers). */
  hurt(e: Enemy, raw: number, def: string): number;
}

/** Fire one of the four. The caller has already paid for it. */
export function castAbility(host: AbilityHost, ability: AbilityId, x: number, y: number): void {
  if (ability === 'q') castVolley(host, x, y);
  else if (ability === 'w') castSurge(host, x, y);
  else if (ability === 'e') castRepair(host, x, y);
  else castUndertow(host, x, y);
}

/** Doc 07 Q — DEPTH-CHARGE VOLLEY: 25 ammo, 6×(40 dmg r2) on r4 over 2 s. */
function castVolley(host: AbilityHost, x: number, y: number): void {
  const q = abilitiesJson.abilities.q;
  const s = host.state;
  for (let k = 0; k < q.charges; k++) {
    // deterministic scatter: seeded rng, staggered impacts over the window
    const a = host.rng.range(0, 1);
    const r = q.targetRadius * Math.sqrt(host.rng.range(0, 1));
    // no trig in the sim: pick a point by rejection-free diamond mapping
    const ox = (a * 4 < 2 ? 1 : -1) * r * (a * 2 % 1);
    const oy = (a * 8 < 4 ? 1 : -1) * r * (1 - (a * 2 % 1));
    const landTick = s.tick + Math.round(((k + 1) / q.charges) * q.overSeconds * TICK_RATE);
    s.projectiles.push({
      id: s.nextId++,
      srcDef: 'ability:q', // abilities earn no tower XP (doc 08)
      x: Math.max(0, Math.min(host.grid.size - 1, x + ox)),
      y: Math.max(0, Math.min(host.grid.size - 1, y + oy)),
      firedTick: s.tick,
      landTick,
      damage: q.chargeDamage,
      radius: q.chargeRadius,
      bombletCount: 0,
      bombletDmg: 0,
      bombletRadius: 1,
      bombletStun: 0,
      knockback: 0,
      slowPct: 0,
      slowDur: 0,
      fromX: x,
      fromY: y - 12, // rains from above
    });
  }
}

/**
 * Doc 07 W — GALVANIC SURGE: 40 capacitor. Everything in r5 is stunned 2 s,
 * takes 20, and any Cloaked fish is dragged into visibility for 4 s. The
 * panic button, and the honest anti-Cloak answer before Laser path C.
 */
function castSurge(host: AbilityHost, x: number, y: number): void {
  const w = abilitiesJson.abilities.w;
  const s = host.state;
  const r2 = w.radius * w.radius;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if ((e.x - x) * (e.x - x) + (e.y - y) * (e.y - y) > r2) continue;
    const dealt = host.hurt(e, w.damage, 'ability:w');
    e.stunUntil = Math.max(e.stunUntil, s.tick + w.stunSeconds * TICK_RATE);
    e.revealedUntil = Math.max(e.revealedUntil, s.tick + w.revealSeconds * TICK_RATE);
    host.emit({ type: 'enemyHit', enemyId: e.id, dmg: dealt, x: e.x, y: e.y, crit: false });
  }
  host.emit({ type: 'explosion', x, y, radius: w.radius });
}

/**
 * Doc 07 E — NANO REPAIR SWARM: 15 alloy. Every structure in r5 mends
 * 20 HP/s for 6 s. Alloy is the universal blood type: material, upgrades,
 * and now medicine.
 */
function castRepair(host: AbilityHost, x: number, y: number): void {
  const e = abilitiesJson.abilities.e;
  const s = host.state;
  const rate = e.healTotal / e.overSeconds;
  const r2 = e.radius * e.radius;
  for (const st of s.structures) {
    if (st.ruin) continue;
    const c = structCentre(st);
    if ((c.x - x) * (c.x - x) + (c.y - y) * (c.y - y) > r2) continue;
    st.healRate = Math.max(st.healRate, rate);
    st.healUntil = Math.max(st.healUntil, s.tick + e.overSeconds * TICK_RATE);
  }
}

/**
 * Doc 07 R — UNDERTOW: 30 capacitor. A lane outward from the cast point,
 * measured along the line from the Core: everything in it is thrown 4 tiles
 * further away and slowed 40% for 2 s. Bosses get the slow only — a boss
 * exam cannot be shoved out of the room.
 */
function castUndertow(host: AbilityHost, x: number, y: number): void {
  const r = abilitiesJson.abilities.r;
  const s = host.state;
  const core = structCentre(host.byId(s.coreId)!);
  let ux = x - core.x;
  let uy = y - core.y;
  const len = Math.sqrt(ux * ux + uy * uy) || 1;
  ux /= len;
  uy /= len;
  const half = r.laneWidth / 2;
  const nsize = host.grid.size - 1;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    const px = e.x - core.x;
    const py = e.y - core.y;
    const along = px * ux + py * uy;
    if (along < len - half) continue; // behind the cast point: not in the lane
    if (Math.abs(px * uy - py * ux) > half) continue; // outside the lane's width
    e.slowPct = Math.max(e.slowPct, r.slowPct);
    e.slowUntil = Math.max(e.slowUntil, s.tick + r.slowSeconds * TICK_RATE);
    if (SPECIES[e.species]!.boss !== null) continue; // bosses stand their ground
    e.x = Math.max(0, Math.min(nsize, e.x + ux * r.pushTiles));
    e.y = Math.max(0, Math.min(nsize, e.y + uy * r.pushTiles));
    e.attackTargetId = -1;
  }
  host.emit({ type: 'explosion', x, y, radius: r.laneWidth });
}
